/**
 * Compliance Report Certificate Lookup
 *
 * Resolves the certificate / license number interpolated into the agency email draft, from the
 * organization's existing certifications. Reads the EXISTING
 * `CertificationService.getOrganizationCertifications(orgId)` → `GET /certifications/organization/:orgId`.
 * No Hub change.
 *
 * Two hard rules govern this file:
 *
 *   1. ⚠️ A wrong-state applicator license is NEVER emitted. An operator working several states
 *      holds several licenses; sending Arkansas your Mississippi number is worse than sending
 *      nothing. No match is a recoverable annoyance, a wrong license number on a state filing
 *      is not.
 *   2. ⚠️ An empty string or `undefined` never reaches the draft. The fallback is always a
 *      visible bracketed placeholder the sender can see and fill in.
 *
 * Reference: /agent-os/specs/2026-08-03-report-agency-email-stopgap/spec.md § 4.1, § 4.6
 */

import { Certification, CertificationStatus } from '../../core/models/certification.model';

// ============================================
// PLACEHOLDERS
// ============================================

/** Emitted when the org has no usable `faa-44807` certification. */
export const FAA_CERTIFICATE_PLACEHOLDER = '[enter exemption / authorization number]';

/** Emitted when no applicator license can be matched to the report's state. */
export const STATE_CERTIFICATE_PLACEHOLDER = '[enter applicator license number]';

// ============================================
// TYPES
// ============================================

/**
 * The single method this util needs. Typed structurally so the real `CertificationService`
 * satisfies it and tests need no Angular TestBed.
 */
export interface OrganizationCertificationsSource {
  getOrganizationCertifications(organizationId: string): Promise<Certification[]>;
}

export interface ResolveReportCertificateParams {
  kind: 'faa' | 'state';
  organizationId: string;
  /** The report's 2-letter state code. Required for state reports, ignored for FAA reports. */
  state?: string | null;
}

/**
 * Ranking for otherwise-equal candidates. `archived` is excluded entirely; an expired number is
 * still the org's real number, so it is usable but ranks last.
 */
const STATUS_RANK: Record<CertificationStatus, number> = {
  active: 0,
  'expiring-soon': 1,
  expired: 2,
  archived: 99
};

// ============================================
// PUBLIC API
// ============================================

/**
 * Fetch the org's certifications and resolve the number for the given report kind.
 *
 * ⚠️ Never throws. A failed fetch resolves to the placeholder so the modal still opens —
 * a missing number is an inconvenience, a modal that won't open is a dead end.
 */
export async function resolveReportCertificateNumber(
  certifications: OrganizationCertificationsSource,
  params: ResolveReportCertificateParams
): Promise<string> {
  const placeholder =
    params.kind === 'state' ? STATE_CERTIFICATE_PLACEHOLDER : FAA_CERTIFICATE_PLACEHOLDER;

  if (!params.organizationId) {
    return placeholder;
  }

  let certs: Certification[];
  try {
    certs = await certifications.getOrganizationCertifications(params.organizationId);
  } catch {
    // Non-blocking by design — see the docstring.
    return placeholder;
  }

  return params.kind === 'state'
    ? resolveStateCertificateNumber(certs, params.state)
    : resolveFaaCertificateNumber(certs);
}

/**
 * FAA exemption / authorization number.
 *
 * Filters `certificationType === 'faa-44807'`, mirroring what Hub does when building the PDF.
 *
 * ⚠️ Reads `certificateNumber`, NOT `licenseNumber`. `licenseNumber` exists in ADN but lives on
 * `adn_profiles`, not on the certification — this is where CropFlight's field name diverges.
 */
export function resolveFaaCertificateNumber(
  certifications: Certification[] | null | undefined
): string {
  const candidates = usableOrgCertifications(certifications, 'faa-44807');
  const best = bestCandidate(candidates);
  return best ? best.certificateNumber.trim() : FAA_CERTIFICATE_PLACEHOLDER;
}

/**
 * State pesticide applicator license number, resolved in strict order:
 *
 *   1. Org-owned `state-applicator` cert whose `state` matches the report's state → use it.
 *   2. Exactly ONE org-owned cert with no `state` (legacy data predating the field) → use it.
 *   3. Otherwise → placeholder.
 *
 * ⚠️ A cert whose `state` is explicitly a DIFFERENT state is never used as a fallback under any
 * input. Two or more null-state certs are ambiguous and also fall through to the placeholder.
 */
export function resolveStateCertificateNumber(
  certifications: Certification[] | null | undefined,
  state: string | null | undefined
): string {
  const candidates = usableOrgCertifications(certifications, 'state-applicator');
  const target = (state ?? '').trim().toUpperCase();

  // 1. Exact state match wins.
  if (target) {
    const exact = bestCandidate(
      candidates.filter((cert) => (cert.state ?? '').trim().toUpperCase() === target)
    );
    if (exact) {
      return exact.certificateNumber.trim();
    }
  }

  // 2. Exactly one legacy cert with no state recorded.
  const stateless = candidates.filter((cert) => (cert.state ?? '').trim().length === 0);
  if (stateless.length === 1) {
    return stateless[0].certificateNumber.trim();
  }

  // 3. Anything else is either absent or ambiguous — never guess.
  return STATE_CERTIFICATE_PLACEHOLDER;
}

// ============================================
// INTERNALS
// ============================================

/**
 * Certifications of the requested type that are actually usable:
 * org-owned (defensively: not explicitly profile-owned), not archived, and carrying a non-empty
 * certificate number — an empty number must never reach the draft.
 */
function usableOrgCertifications(
  certifications: Certification[] | null | undefined,
  type: Certification['certificationType']
): Certification[] {
  if (!Array.isArray(certifications)) {
    return [];
  }

  return certifications.filter(
    (cert) =>
      !!cert &&
      cert.certificationType === type &&
      cert.ownerType !== 'profile' &&
      cert.status !== 'archived' &&
      typeof cert.certificateNumber === 'string' &&
      cert.certificateNumber.trim().length > 0
  );
}

/**
 * Best of a candidate set: active beats expiring-soon beats expired, then most recently issued,
 * then id for a deterministic result.
 */
function bestCandidate(candidates: Certification[]): Certification | null {
  if (candidates.length === 0) {
    return null;
  }

  return [...candidates].sort((a, b) => {
    const rank = statusRank(a.status) - statusRank(b.status);
    if (rank !== 0) {
      return rank;
    }

    const issued = issuedTime(b.issuedDate) - issuedTime(a.issuedDate);
    if (issued !== 0) {
      return issued;
    }

    return (a.id ?? '').localeCompare(b.id ?? '');
  })[0];
}

function statusRank(status: CertificationStatus | undefined): number {
  return status && status in STATUS_RANK ? STATUS_RANK[status] : 50;
}

function issuedTime(issuedDate: string | null | undefined): number {
  if (!issuedDate) {
    return 0;
  }
  const parsed = new Date(issuedDate).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}
